import React, { useEffect } from 'react';

import {
  AmountTermValueType,
  DueAmountFormats,
  GlobalState,
  usePaymentTermsInputContext,
} from '../contexts/CreditStatementTermsContext';
import { hasCrossedPaymentLinkMaxMinAmount } from '../utils';
import {
  StyledInput,
  StyledInputContainer,
  StyledInputIcon,
  StyledInputRow,
  StyledLabelContainer,
  StyledSelect,
  StyledSelectContainer,
} from './Common.style';

export function AmountDueInput() {
  const { setGlobalState, globalState, getDefaultAmountDue, getDueAmount } =
    usePaymentTermsInputContext();

  const handleAmountDueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, id: targetId },
    } = event;

    const { amountTermValueType, amountTermValue: prevAmountTermValue } = globalState;
    let positiveInputValue = value.replace('-', '');

    const isFixedAmountInput = targetId === 'view_custom_account_statement_amount_due';

    // If percentage value is more than 100, keep the previous value.
    if (
      isFixedAmountInput &&
      amountTermValueType === AmountTermValueType.PERCENTAGE &&
      parseFloat(positiveInputValue) > 100
    ) {
      positiveInputValue = prevAmountTermValue;
    }

    // Always accept input with max 2 decimals.
    if (isFixedAmountInput && amountTermValueType === AmountTermValueType.FIXED_AMOUNT) {
      const shouldTruncate = positiveInputValue.split('.')[1]?.length > 2;
      if (shouldTruncate) {
        // Use math.trunc instead of math.round to avoid rounding errors.
        positiveInputValue = (Math.trunc(parseFloat(positiveInputValue) * 100) / 100).toString();
      }

      setGlobalState((prevState: GlobalState) => ({
        ...prevState,
        amountTermValue: positiveInputValue,
      }));
      return;
    }

    setGlobalState((prevState: GlobalState) => ({
      ...prevState,
      amountTermValue: positiveInputValue,
    }));
  };

  const handleChangeAmountDueType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      target: { value },
    } = event;

    setGlobalState((prevState: GlobalState) => ({
      ...prevState,
      amountTermValueType: value as AmountTermValueType,
    }));
  };

  // Handle cases where the user leaves an empty input field.
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    if (value === '') {
      setGlobalState((prevState: GlobalState) => ({
        ...prevState,
        amountTermValue: getDefaultAmountDue(),
      }));
    }
  };

  useEffect(() => {
    setGlobalState((prevState: GlobalState) => ({
      ...prevState,
      amountTermValue: getDefaultAmountDue(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.amountTermValueType]);

  const { belowMinAmount, overMaxAmount } = hasCrossedPaymentLinkMaxMinAmount(
    getDueAmount(DueAmountFormats.CENTS)
  );

  const inputFieldHighlightStyle =
    globalState.hasLightSpeedPayments && (belowMinAmount || overMaxAmount)
      ? { border: '1px solid #EA891E' }
      : undefined;

  const currencySymbol = window.merchantos.locale.currency_symbol;
  const currencySymbolLength = currencySymbol.length;

  return (
    <StyledInputRow>
      <StyledLabelContainer className="label">
        <label htmlFor="view_custom_account_statement_amount_due">Amount due</label>
      </StyledLabelContainer>
      {globalState.amountTermValueType === AmountTermValueType.FIXED_AMOUNT ? (
        <StyledInputContainer>
          <StyledInput
            type="number"
            autoComplete="off"
            data-test={`amount-due-input${inputFieldHighlightStyle ? '-highlighted' : ''}`}
            style={inputFieldHighlightStyle}
            value={globalState.amountTermValue}
            id="view_custom_account_statement_amount_due"
            name="view_custom_account_statement_amount_due_fixed"
            className="view_view number data_control"
            onChange={handleAmountDueChange}
            onBlur={handleBlur}
          />
          <StyledInputIcon>
            <span
              className="notranslate"
              style={{ padding: '0px', fontSize: currencySymbolLength >= 3 ? '10px' : '14px' }}
            >
              {currencySymbol}
            </span>
          </StyledInputIcon>
        </StyledInputContainer>
      ) : (
        <StyledInputContainer>
          <StyledInput
            data-test={`amount-due-input${inputFieldHighlightStyle ? '-highlighted' : ''}`}
            style={inputFieldHighlightStyle}
            type="number"
            id="view_custom_account_statement_amount_due"
            name="credit_account_amount_due_specific_input"
            value={globalState.amountTermValue}
            onChange={handleAmountDueChange}
            onBlur={handleBlur}
          />
          <StyledInputIcon>%</StyledInputIcon>
        </StyledInputContainer>
      )}
      <StyledSelectContainer>
        <StyledSelect
          data-test="amount-due-type-select"
          name="custom_account_statement_amount_due_select"
          id="view_custom_account_statement_amount_due_select_type"
          className="view_view data_control"
          defaultValue={globalState.amountTermValueType}
          onChange={handleChangeAmountDueType}
        >
          <option value={AmountTermValueType.PERCENTAGE}>Percentage</option>
          <option value={AmountTermValueType.FIXED_AMOUNT}>Fixed Amount</option>
        </StyledSelect>
      </StyledSelectContainer>
    </StyledInputRow>
  );
}
